/*
 * cart-drawer.js
 *
 * Les deux classes de Dawn (CartDrawer, CartDrawerItems) sont conservees
 * telles quelles ; ce fichier les ETEND et n'enregistre que les sous-classes.
 * Tout ce qui est propre a ce theme vit dans CartDrawerRecords :
 * onglets Panier/Favoris, suggestions, code de reduction, estimation des
 * frais, bande « article retire » et « garder pour plus tard ».
 *
 * Les mises a jour passent toutes par l'API Ajax de Dawn et le rendu de
 * sections (sections=cart-drawer,cart-icon-bubble) : le panneau entier est
 * remplace, donc la barre de livraison, le resume, les suggestions et le
 * compteur de l'icone se rafraichissent ensemble, sans code de synchro.
 */
class CartDrawer extends HTMLElement {
  constructor() {
    super();

    this.addEventListener('keyup', (evt) => evt.code === 'Escape' && this.close());
    this.querySelector('#CartDrawer-Overlay').addEventListener('click', this.close.bind(this));
    this.setHeaderCartIconAccessibility();
  }

  setHeaderCartIconAccessibility() {
    const cartLink = document.querySelector('#cart-icon-bubble');
    if (!cartLink) return;

    cartLink.setAttribute('role', 'button');
    cartLink.setAttribute('aria-haspopup', 'dialog');
    cartLink.addEventListener('click', (event) => {
      event.preventDefault();
      this.open(cartLink);
    });
    cartLink.addEventListener('keydown', (event) => {
      if (event.code.toUpperCase() === 'SPACE') {
        event.preventDefault();
        this.open(cartLink);
      }
    });
  }

  open(triggeredBy) {
    if (this.classList.contains('active')) return;
    if (triggeredBy) this.setActiveElement(triggeredBy);
    const cartDrawerNote = this.querySelector('[id^="Details-"] summary');
    if (cartDrawerNote && !cartDrawerNote.hasAttribute('role')) this.setSummaryAccessibility(cartDrawerNote);
    // here the animation doesn't seem to always get triggered. A timeout seem to help
    setTimeout(() => {
      this.classList.add('animate', 'active');
    });

    this.addEventListener(
      'transitionend',
      () => {
        const containerToTrapFocusOn = this.classList.contains('is-empty')
          ? this.querySelector('.drawer__inner-empty')
          : document.getElementById('CartDrawer');
        const focusElement = this.querySelector('.drawer__inner') || this.querySelector('.drawer__close');
        trapFocus(containerToTrapFocusOn, focusElement);
      },
      { once: true },
    );

    document.body.classList.add('overflow-hidden');

    // cart-drawer-items is a CartItems subclass that extends createViewEventElement.
    // Its `view-event-trigger="manual"` skips auto-dispatch on connect; we fire
    // it here when the drawer opens, with `context: 'dialog'` from the payload attribute.
    this.querySelector('cart-drawer-items')?.dispatchViewEvent();
  }

  close() {
    this.classList.remove('active');
    removeTrapFocus(this.activeElement);
    document.body.classList.remove('overflow-hidden');
  }

  setSummaryAccessibility(cartDrawerNote) {
    cartDrawerNote.setAttribute('role', 'button');
    cartDrawerNote.setAttribute('aria-expanded', 'false');

    if (cartDrawerNote.nextElementSibling.getAttribute('id')) {
      cartDrawerNote.setAttribute('aria-controls', cartDrawerNote.nextElementSibling.id);
    }

    cartDrawerNote.addEventListener('click', (event) => {
      event.currentTarget.setAttribute('aria-expanded', !event.currentTarget.closest('details').hasAttribute('open'));
    });

    cartDrawerNote.parentElement.addEventListener('keyup', onKeyUpEscape);
  }

  renderContents(parsedState) {
    this.querySelector('.drawer__inner').classList.contains('is-empty') &&
      this.querySelector('.drawer__inner').classList.remove('is-empty');
    this.productId = parsedState.id;
    this.getSectionsToRender().forEach((section) => {
      const sectionElement = section.selector
        ? document.querySelector(section.selector)
        : document.getElementById(section.id);

      if (!sectionElement) return;
      sectionElement.innerHTML = this.getSectionInnerHTML(parsedState.sections[section.id], section.selector);
    });

    setTimeout(() => {
      this.querySelector('#CartDrawer-Overlay').addEventListener('click', this.close.bind(this));
      this.open();
    });
  }

  getSectionInnerHTML(html, selector = '.shopify-section') {
    return new DOMParser().parseFromString(html, 'text/html').querySelector(selector).innerHTML;
  }

  getSectionsToRender() {
    return [
      {
        id: 'cart-drawer',
        selector: '#CartDrawer',
      },
      {
        id: 'cart-icon-bubble',
      },
    ];
  }

  getSectionDOM(html, selector = '.shopify-section') {
    return new DOMParser().parseFromString(html, 'text/html').querySelector(selector);
  }

  setActiveElement(element) {
    this.activeElement = element;
  }
}

/* ------------------------------------------------------------------ */

class CartDrawerRecords extends CartDrawer {
  constructor() {
    super();
    this.derniereSuppression = null;
    this.minuteurAnnulation = null;
    this.brancher();
  }

  connectedCallback() {
    this.rendreFavoris();
    this.estimerLivraison();
    if (this.resyncBranche) return;
    this.resyncBranche = true;
    // Retour arriere (cache du navigateur) ou retour sur l'onglet : on relit le panier.
    const relire = () => this.rafraichir().catch(() => {});
    window.addEventListener('pageshow', (e) => { if (e.persisted) relire(); });
    document.addEventListener('visibilitychange', () => { if (document.visibilityState === 'visible') relire(); });
  }

  /* Sur mobile le panneau s'arrete sous la barre d'annonce : si l'en-tete
     etait retracte par le defilement, cette bande serait vide et laisserait
     voir la page. On redeploie donc le groupe a l'ouverture, comme la
     reference qui garde sa barre visible au-dessus du tiroir. */
  open(triggeredBy) {
    document.querySelector('.smart-header-group')?.classList.remove('is-collapsed');
    super.open(triggeredBy);
    // Ouverture par l'icone du panier : le contenu a pu changer depuis (autre onglet, retour arriere).
    if (triggeredBy) this.rafraichir().catch(() => {});
  }

  /* Delegation unique : le panneau est remplace en entier a chaque mise a
     jour, donc aucun ecouteur pose sur son contenu ne survivrait. */
  brancher() {
    /* Phase de CAPTURE : a quantite 1, le moins supprime la ligne au lieu de
       descendre a 0, que Dawn refuserait (quantity_rule.min vaut 1). En
       capture, on passe avant l'ecouteur de <quantity-input> et on l'empeche
       de s'executer. L'attribut min du champ vaut 0 pour que le bouton reste
       cliquable a 1 : desactive, il n'emettrait aucun clic. */
    this.addEventListener(
      'click',
      (evt) => {
        const cible = evt.target instanceof Element ? evt.target : null;

        /* « + » : si le champ affiche autre chose que la quantite reelle de la ligne
           (ex. un 0 reste bloque apres une erreur), on repart de la quantite reelle. */
        const plus = cible?.closest('.cart-drawer__qty-button[name="plus"]');
        if (plus) {
          const ligneP = plus.closest('.cart-drawer__item');
          const champP = ligneP?.querySelector('.cart-drawer__qty-input');
          const reelle = Number(ligneP?.dataset.quantite);
          if (champP && reelle > 0 && Number(champP.value) !== reelle) {
            champP.value = String(reelle);
            champP.setCustomValidity('');
          }
          return;
        }

        const moins = cible?.closest('.cart-drawer__qty-button[name="minus"]');
        if (!moins) return;

        const ligne = moins.closest('.cart-drawer__item');
        const champ = ligne?.querySelector('.cart-drawer__qty-input');
        if (!ligne || !champ || Number(champ.value) > 1) return;

        evt.preventDefault();
        evt.stopPropagation();
        this.supprimerLigne(ligne);
      },
      true
    );

    this.addEventListener('click', (evt) => {
      const cible = evt.target instanceof Element ? evt.target : null;
      if (!cible) return;

      const onglet = cible.closest('[data-onglet]');
      if (onglet) return this.basculerOnglet(onglet.dataset.onglet);

      const ajout = cible.closest('[data-ajouter-upsell]');
      if (ajout) return this.ajouterVariante(ajout.dataset.variante, 1, ajout);

      const coeur = cible.closest('[data-garder]');
      if (coeur) return this.garderPourPlusTard(coeur);

      const annuler = cible.closest('[data-annuler]');
      if (annuler) return this.annulerSuppression();

      const ajoutFavori = cible.closest('[data-favori-ajouter]');
      if (ajoutFavori) return this.ajouterVariante(ajoutFavori.dataset.variante, 1, ajoutFavori, true);

      const retraitFavori = cible.closest('[data-favori-retirer]');
      if (retraitFavori) {
        window.CartDrawerFavoris.retirer(retraitFavori.dataset.variante);
        this.rendreFavoris();
      }
    });

    this.addEventListener('submit', (evt) => {
      const formulaire = evt.target.closest('[data-formulaire-reduction]');
      if (!formulaire) return;
      evt.preventDefault();
      this.appliquerReduction(formulaire);
    });
  }

  /* --- Onglets Panier / Favoris --- */

  basculerOnglet(nom) {
    this.querySelectorAll('[data-onglet]').forEach((bouton) => {
      const actif = bouton.dataset.onglet === nom;
      bouton.classList.toggle('is-active', actif);
      bouton.setAttribute('aria-selected', String(actif));
    });
    this.querySelectorAll('[data-panneau]').forEach((panneau) => {
      panneau.hidden = panneau.dataset.panneau !== nom;
    });
    if (nom === 'wishlist') this.rendreFavoris();
  }

  rendreFavoris() {
    const liste = this.querySelector('[data-liste-favoris]');
    if (!liste || !window.CartDrawerFavoris) return;
    window.CartDrawerFavoris.rendre(liste, {
      libelleAjouter: this.dataset.libelleAjouter,
      libelleRetirer: this.dataset.libelleRetirerFavori,
      texteVide: this.dataset.texteFavorisVide,
    });
  }

  /* --- Mises a jour du panier --- */

  static SECTIONS = 'cart-drawer,cart-icon-bubble';

  /* Remplace le panneau et le compteur a partir des sections renvoyees par
     l'API. Le panneau porte tout : barre de livraison, liste, suggestions,
     code promo et resume se rafraichissent donc ensemble. */
  appliquerSections(sections) {
    if (!sections) return false;

    const panneau = this.querySelector('.cart-drawer__panel');
    const nouveauPanneau = sections['cart-drawer']
      ? this.getSectionDOM(sections['cart-drawer'], '.cart-drawer__panel')
      : null;
    if (panneau && nouveauPanneau) panneau.innerHTML = nouveauPanneau.innerHTML;

    const bulle = document.getElementById('cart-icon-bubble');
    if (bulle && sections['cart-icon-bubble']) {
      bulle.innerHTML = this.getSectionInnerHTML(sections['cart-icon-bubble'], '.shopify-section');
    }

    this.classList.toggle('is-empty', this.querySelector('.cart-drawer__items') === null);
    this.rendreFavoris();
    this.estimerLivraison();
    return Boolean(nouveauPanneau);
  }

  /* Repli quand l'appel n'a pas pu renvoyer les sections (code de reduction,
     erreur reseau) : une requete de rendu dediee. */
  async rafraichir() {
    const reponse = await fetch(`${window.location.pathname}?sections=${CartDrawerRecords.SECTIONS}`);
    this.appliquerSections(await reponse.json());
  }

  async ajouterVariante(varianteId, quantite, bouton, depuisFavoris = false) {
    if (!varianteId) return;
    if (bouton) bouton.setAttribute('aria-disabled', 'true');
    try {
      const reponse = await fetch(routes.cart_add_url, {
        ...fetchConfig('javascript'),
        body: JSON.stringify({
          items: [{ id: Number(varianteId), quantity: quantite }],
          sections: CartDrawerRecords.SECTIONS,
          sections_url: window.location.pathname,
        }),
      });
      const resultat = await reponse.json();
      if (resultat.status) throw new Error(resultat.description || resultat.message);
      if (depuisFavoris) window.CartDrawerFavoris.retirer(varianteId);
      if (!this.appliquerSections(resultat.sections)) await this.rafraichir();
      this.basculerOnglet('cart');
    } catch (e) {
      const zone = this.querySelector('#CartDrawer-CartErrors');
      if (zone) zone.textContent = window.cartStrings?.error || '';
    } finally {
      if (bouton) bouton.removeAttribute('aria-disabled');
    }
  }

  /* La ligne est designee par sa cle (data-cle) quand on l'a : son numero change
     des qu'une ligne masquee (stickers offerts) est inseree ou retiree. */
  async changerLigne(ligne, quantite, cle) {
    const reponse = await fetch(routes.cart_change_url, {
      ...fetchConfig(),
      body: JSON.stringify({
        ...(cle ? { id: cle } : { line: Number(ligne) }),
        quantity: quantite,
        sections: CartDrawerRecords.SECTIONS,
        sections_url: window.location.pathname,
      }),
    });
    const resultat = JSON.parse(await reponse.text());
    if (!this.appliquerSections(resultat.sections)) await this.rafraichir();
  }

  /* --- Garder pour plus tard : retire la ligne et la met en favoris --- */

  async garderPourPlusTard(bouton) {
    const ligne = bouton.closest('.cart-drawer__item');
    if (!ligne) return;
    const d = ligne.dataset;

    window.CartDrawerFavoris?.ajouter({
      variante: d.variante,
      titre: d.titre,
      varianteTitre: d.varianteTitre,
      url: d.url,
      prix: d.prix,
      image: d.image,
    });

    await this.supprimerLigne(ligne);
  }

  /* Retire la ligne et garde de quoi la remettre : la bande « article retire »
     propose d'annuler pendant six secondes. */
  async supprimerLigne(ligne) {
    const d = ligne.dataset;
    this.derniereSuppression = {
      variante: d.variante,
      quantite: Number(d.quantite) || 1,
      image: d.image,
    };
    await this.changerLigne(d.ligne, 0, d.cle);
    this.afficherAnnulation();
  }

  afficherAnnulation() {
    if (this.dataset.annulation !== 'true') return;
    const bande = this.querySelector('[data-bande-annulation]');
    if (!bande || !this.derniereSuppression) return;

    const image = bande.querySelector('[data-image-annulation]');
    if (image && this.derniereSuppression.image) image.src = this.derniereSuppression.image;

    bande.hidden = false;
    bande.classList.remove('is-fading');
    clearTimeout(this.minuteurAnnulation);
    this.minuteurAnnulation = setTimeout(() => {
      bande.classList.add('is-fading');
      setTimeout(() => {
        bande.hidden = true;
        this.derniereSuppression = null;
      }, 300);
    }, 6000);
  }

  async annulerSuppression() {
    if (!this.derniereSuppression) return;
    const { variante, quantite } = this.derniereSuppression;
    window.CartDrawerFavoris?.retirer(variante);
    this.derniereSuppression = null;
    clearTimeout(this.minuteurAnnulation);
    await this.ajouterVariante(variante, quantite, null);
  }

  /* --- Code de reduction ---
     L'API Ajax du panier ne sait pas appliquer un code : on pose le cookie de
     remise via /discount/<code>, puis on re-rend la section. Si le nombre de
     remises appliquees n'a pas bouge, le code est refuse et le message
     s'affiche sous le champ, sans redirection. */
  async appliquerReduction(formulaire) {
    const champ = formulaire.querySelector('input[name="discount"]');
    const erreur = this.querySelector('[data-erreur-reduction]');
    const code = (champ?.value || '').trim();
    if (!code) return;

    const avant = this.querySelectorAll('.cart-drawer__discount-applied').length;
    if (erreur) erreur.hidden = true;

    try {
      await fetch(`/discount/${encodeURIComponent(code)}`, { redirect: 'follow' });
    } catch (e) {
      /* le cookie peut avoir ete pose malgre l'erreur reseau : on re-rend quand meme */
    }

    await this.rafraichir();

    const apres = this.querySelectorAll('.cart-drawer__discount-applied').length;
    const nouvelleErreur = this.querySelector('[data-erreur-reduction]');
    if (apres > avant) {
      const nouveauChamp = this.querySelector('input[name="discount"]');
      if (nouveauChamp) nouveauChamp.value = '';
    } else if (nouvelleErreur) {
      nouvelleErreur.textContent = nouvelleErreur.dataset.message;
      nouvelleErreur.hidden = false;
    }
  }

  /* --- Estimation des frais de livraison ---
     En cas d'echec (pays non desservi, panier vide, reseau), on ne touche a
     rien : la mention « calcules au paiement » reste affichee. */
  async estimerLivraison() {
    if (this.dataset.modeLivraison !== 'estimation') return;
    const cible = this.querySelector('[data-valeur-livraison]');
    if (!cible) return;

    const parametres = new URLSearchParams();
    if (this.dataset.pays) parametres.set('shipping_address[country]', this.dataset.pays);
    if (this.dataset.province) parametres.set('shipping_address[province]', this.dataset.province);
    if (this.dataset.codePostal) parametres.set('shipping_address[zip]', this.dataset.codePostal);

    try {
      const reponse = await fetch(`${routes.cart_url}/shipping_rates.json?${parametres}`);
      if (!reponse.ok) return;
      const donnees = await reponse.json();
      const tarifs = donnees.shipping_rates || [];
      if (tarifs.length === 0) return;

      const moinsCher = tarifs.reduce((a, b) => (parseFloat(a.price) <= parseFloat(b.price) ? a : b));
      cible.textContent = this.formaterMontant(parseFloat(moinsCher.price));
    } catch (e) {
      /* silence volontaire : la mention par defaut reste en place */
    }
  }

  formaterMontant(valeur) {
    const devise = window.Shopify?.currency?.active || 'EUR';
    const langue = document.documentElement.lang || 'fr';
    try {
      return new Intl.NumberFormat(langue, { style: 'currency', currency: devise }).format(valeur);
    } catch (e) {
      return `${valeur} ${devise}`;
    }
  }

  /* Rendu declenche par product-form.js apres un ajout depuis une fiche
     produit : on vise le panneau, pas seulement #CartDrawer. */
  getSectionsToRender() {
    return [
      { id: 'cart-drawer', selector: '#CartDrawer' },
      { id: 'cart-icon-bubble' },
    ];
  }
}

customElements.define('cart-drawer', CartDrawerRecords);

/* ------------------------------------------------------------------ */

class CartDrawerItems extends CartItems {
  /* Le panneau contient la barre de livraison, la liste, les suggestions, le
     code promo et le resume : le remplacer rafraichit tout d'un coup. */
  getSectionsToRender() {
    return [
      {
        id: 'CartDrawer',
        section: 'cart-drawer',
        selector: '.cart-drawer__panel',
      },
      {
        id: 'cart-icon-bubble',
        section: 'cart-icon-bubble',
        selector: '.shopify-section',
      },
    ];
  }
}

customElements.define('cart-drawer-items', CartDrawerItems);
